import { extractUnitLocatorsFromText } from './scope-unit-locators';
import { filterInclusionLines } from '../services/scope-subheadings';

// Inclusions are stored either as plain strings or as `{ text, ... }` objects.
type InclusionLike = string | { text?: unknown } | null | undefined;
// Scope items are loosely-shaped JSONB-ish records (camelCase + snake_case
// variants of the same fields coexist). Model them as open records.
type ScopeItemLike = Record<string, unknown>;
// The two shared-site logistics categories this module dedupes.
type LogisticsKind = 'siteProtection' | 'cleanup';

function textOfInclusion(inclusion: InclusionLike): string {
    if (typeof inclusion === 'string') return inclusion;
    if (inclusion && typeof inclusion === 'object' && typeof inclusion.text === 'string') return inclusion.text;
    return '';
}

function normaliseText(value: unknown): string {
    return String(value || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function searchableItemText(item: ScopeItemLike = {}): string {
    return normaliseText([
        item.title,
        item.description,
        item.descriptionBrief,
        item.description_brief,
        item.clientDescription,
        item.notes_to_client,
    ].filter(Boolean).join(' '));
}

function itemLocatorText(item: ScopeItemLike = {}): string {
    return [
        item.title,
        item.description,
        item.descriptionBrief,
        item.description_brief,
        item.clientDescription,
        item.notes_to_client,
    ].filter(Boolean).join(' ');
}

export function classifySharedSiteLogisticsInclusion(inclusion: InclusionLike): LogisticsKind | null {
    // A subheading ("BALCONY - SITE PROTECTION AND TRANSIT SETUP") names a
    // group of lines. It is never the protection or cleanup line itself, so it
    // is neither dropped as a repeat nor allowed to stand in for one.
    if (inclusion && typeof inclusion === 'object' && (inclusion as { type?: unknown }).type === 'heading') return null;
    const text = normaliseText(textOfInclusion(inclusion));
    if (!text) return null;

    const hasProtection = /\bprotection\b/.test(text);
    const hasTemporary = /\btemporary\b/.test(text);
    // Plurals too: the contract's protection form ends "(floors and finishes)",
    // which the singular list did not read as site protection (F44).
    const hasSiteOrWorkArea = /\b(sites?|floors?|surfaces?|work areas?|work zones?|immediate work)\b/.test(text);
    if ((hasProtection && hasTemporary && hasSiteOrWorkArea)
        || /\bfloor site protection\b/.test(text)
        || /\bsite protection\b/.test(text)) {
        return 'siteProtection';
    }

    const hasCleanupVerb = /\b(clean up|cleanup|clean down|clean)\b/.test(text);
    const hasCompletion = /\b(completion|completion of works|on completion|at completion)\b/.test(text);
    const hasRubbish = /\b(rubbish|work related rubbish|work associated rubbish|spoil|debris)\b/.test(text);
    const hasArea = /\b(sites?|work areas?|immediate work areas?)\b/.test(text);
    if ((hasCleanupVerb && hasArea && (hasCompletion || hasRubbish))
        || /\bsite clean up\b/.test(text)
        || /\bsite cleanup\b/.test(text)
        || /\bsite clean up and removal\b/.test(text)) {
        return 'cleanup';
    }

    return null;
}

function extractUnitKeys(item: ScopeItemLike): string[] {
    return extractUnitLocatorsFromText(itemLocatorText(item)).map((locator: { key: string }) => locator.key);
}

function extractOptionKey(item: ScopeItemLike, index: number): string {
    const text = searchableItemText(item);
    const labelled = text.match(/\b(option|alternate|alternative|variant)\s*#?\s*([a-z]|\d+)\b/);
    if (labelled) return `${labelled[1]}-${labelled[2]}`;
    if (/\b(optional|provisional|client option|upgrade option)\b/.test(text)
        || item?.is_recommendation === true
        || item?.isRecommendation === true
        || item?.recommendation === true) {
        return `option-item-${index}`;
    }
    return '';
}

function isManualScopeItem(item: ScopeItemLike | null | undefined): boolean {
    return item?.manuallyEdited === true
        || item?.editedBy === 'user'
        || item?.editedBy === 'assistant'
        || !!item?.humanEditedAt
        || !!item?.inclusionsEditedAt;
}

function addSeenLogistics(seen: Set<LogisticsKind>, inclusions: unknown = []): void {
    for (const inclusion of Array.isArray(inclusions) ? inclusions : []) {
        const kind = classifySharedSiteLogisticsInclusion(inclusion);
        if (kind) seen.add(kind);
    }
}

export function dedupeSharedSiteLogistics(scopeItems: unknown, options: { preserveManual?: boolean } = {}): unknown {
    if (!Array.isArray(scopeItems) || scopeItems.length <= 1) return scopeItems;
    const preserveManual = options.preserveManual !== false;
    const unitKeys = scopeItems.flatMap(extractUnitKeys).filter(Boolean);
    const hasDistinctUnitScopes = new Set(unitKeys).size >= 2;
    const seenByGroup = new Map<string, Set<LogisticsKind>>();
    let changed = false;

    const groupForItem = (item: ScopeItemLike, index: number): string => {
        const optionKey = extractOptionKey(item, index);
        if (optionKey) return `option:${optionKey}`;
        const unitKey = hasDistinctUnitScopes ? extractUnitKeys(item).join('+') : '';
        if (unitKey) return `unit:${unitKey}`;
        return 'shared-site';
    };

    const nextItems = scopeItems.map((item, index) => {
        if (!item || typeof item !== 'object' || !Array.isArray(item.inclusions)) return item;
        const groupKey = groupForItem(item, index);
        if (!seenByGroup.has(groupKey)) seenByGroup.set(groupKey, new Set<LogisticsKind>());
        // `!` is compile-erased: the line above guarantees the key exists.
        const seen = seenByGroup.get(groupKey)!;

        if (preserveManual && isManualScopeItem(item)) {
            addSeenLogistics(seen, item.inclusions);
            return item;
        }

        // A heading whose only line was a repeated protection or cleanup
        // line goes with it (filterInclusionLines), never left empty.
        const nextInclusions = filterInclusionLines(item.inclusions as unknown[], (inclusion) => {
            const kind = classifySharedSiteLogisticsInclusion(inclusion as InclusionLike);
            if (kind && seen.has(kind)) {
                changed = true;
                return false;
            }
            if (kind) seen.add(kind);
            return true;
        });

        if (nextInclusions.length === item.inclusions.length) return item;
        return { ...item, inclusions: nextInclusions };
    });

    return changed ? nextItems : scopeItems;
}
